import { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { Button } from "@gpstrackit/react-controls";

import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';

import { toggleDealerWebhookStatus, reset } from "./redux/slice";
import { openModalCreateEditWebHook } from 'Modules/dealers/.globals/redux/actions';
import { openModalDeleteWebHook } from 'Modules/dealers/.globals/redux/actions';
import { HasPermission } from 'Core/security';
import { getDateFormatted } from 'Core/data/Helpers';

import CreateEditWebHook from '../createEditWebHook';
import DeleteWebHook from '../deleteWebHook';
import { NotificationManager } from 'Components/Notifications';
import { fetchWebhookDetails } from '../createEditWebHook/redux/slice';

const WebHooksTab = (props) => {
    const { dealerId, tab, height = 400 } = props;
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const tableRef = useRef(null);

    const user = useSelector(state => state.securityRedux.user);
    const { openModal: openModalCreateEdit } = useSelector(state => state.createEditWebHookRedux);
    const { openModal: openModalDelete } = useSelector(state => state.deleteWebHookRedux);

    const canCreate = HasPermission(user, "dwbhook", "c");
    const canUpdate = HasPermission(user, "dwbhook", "u");
    const canDelete = HasPermission(user, "dwbhook", "d");

    useEffect(() => {
        return () => {
            dispatch(reset());
        };
    }, [dispatch]);

    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort) => {
        let currentConditions = {}
        let currentTags = {}

        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name' && item?.fields[0] !== 'dealerId');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }

        let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");

        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.name))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: ["name"], terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        currentConditions.push({ fields: ["dealerId"], terms: [dealerId], exact: true, conditionOperator: "AND" });

        tableRef?.current?.getData({ newSort: sort, newConditions: currentConditions, newColumns });
    };

    const handleToggleStatus = async (webhook) => {
        await dispatch(toggleDealerWebhookStatus({
            dealerId,
            webhookId: webhook.id,
            currentStatus: webhook.status,
        }));
        tableRef?.current?.getData({});
    };

    const handleOpenCreate = () => {
        dispatch(openModalCreateEditWebHook({ dealerId, webhook: null }));
    };

    const handleOpenEdit = (webhook) => {
        // Open modal immediately with grid data (will show loading)
        dispatch(openModalCreateEditWebHook({ dealerId, webhook }));
        // Fetch complete webhook data via Redux thunk
        dispatch(fetchWebhookDetails({ dealerId, webhookId: webhook.id }));
    };

    const handleOpenDelete = (webhook) => {
        dispatch(openModalDeleteWebHook({ dealerId, webhook }));
    };

    const statusChip = (status, webhook) => {
        const isActive = Number(status) === 1;
        return (
            <span
                style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    backgroundColor: isActive ? '#4caf50' : '#bdbdbd',
                    color: '#ffffff',
                    cursor: canUpdate ? 'pointer' : 'default',
                    display: 'inline-block',
                }}
                onClick={() => canUpdate && handleToggleStatus(webhook)}
            >
                {isActive ? messages['web_hooks_status_active'] || 'Active' : messages['web_hooks_status_inactive'] || 'Cancelled'}
            </span>
        );
    };

    const [columns, setColumns] = useState([
        {
            key: 'id',
            field: 'id',
            title: messages['web_hooks_id'] || 'ID',
            width: 80,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            code: "web_hooks_id",
            render: (item) => <span>{item?.id}</span>,
            functionString: 'return row?.id'
        },
        {
            key: 'name',
            field: 'name',
            title: messages['web_hooks_name'] || 'Name',
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "web_hooks_name",
            render: (item) => <span>{item?.name}</span>,
            functionString: 'return row?.name'
        },
        {
            key: 'url',
            field: 'url',
            title: messages['web_hooks_action'] || 'Url',
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "web_hooks_action",
            render: (item) => (
                <span
                    title={item?.url}
                    style={{
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        display: 'block',
                    }}
                >
                    {item?.url}
                </span>
            ),
            functionString: 'return row?.url'
        },
        {
            key: 'eventType',
            field: 'eventType',
            title: messages['web_hooks_event_type'] || 'Event Type',
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "web_hooks_event_type",
            render: (item) => <span>{item?.eventType}</span>,
            functionString: 'return row?.eventType'
        },
        {
            key: 'status',
            field: 'status',
            title: messages['web_hooks_status'] || 'Status',
            width: 120,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "web_hooks_status",
            render: (item) => statusChip(item?.status, item),
            functionString: 'return row?.status'
        },
        {
            key: 'createdOn',
            field: 'createdOn',
            title: messages['web_hooks_created_on'] || 'Created On',
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "web_hooks_created_on",
            render: (item) => <span>{getDateFormatted(item?.createdOn, "date", "datetime")}</span>,
            functionString: 'return row?.createdOn'
        },
        {
            key: 'updatedOn',
            field: 'updatedOn',
            title: messages['web_hooks_updated_on'] || 'Updated On',
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "web_hooks_updated_on",
            render: (item) => <span>{getDateFormatted(item?.updatedOn, "date", "datetime")}</span>,
            functionString: 'return row?.updatedOn'
        },
    ]);

    const getMenuOptions = useCallback(() => {
        return [
            messages['web_hooks_edit'],
            messages['web_hooks_delete']
        ]
    }, [messages]);

    const handleClickMenu = useCallback((event, item) => {
        // item can be an array when multiple items are selected, extract first one
        const webhook = Array.isArray(item) ? item[0] : item;

        if (event === messages['web_hooks_edit']) {
            if (canUpdate) {
                handleOpenEdit(webhook);
            }
        }
        if (event === messages['web_hooks_delete']) {
            if (canDelete) {
                handleOpenDelete(webhook);
            }
        }
    }, [messages, canUpdate, canDelete, handleOpenEdit, handleOpenDelete]);

    const handleChangeColumns = useCallback((items) => {
        let currentColumns = items.map((item) => ({
            ...item,
            title: item.code ? messages[item.code] : item.title,
            render: item.render,
        }));
        setColumns(currentColumns);
    }, [messages]);

    return (
        <div style={{ padding: '10px' }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                name="pm-dealer-webhooks-tab"
                entity="dealer-webhooks-tab"
                columns={columns}
                menuOptions={getMenuOptions}
                onClickMenu={handleClickMenu}
                infiniteScroll={false}
                graphqlConfig={{
                    function: "searchDealerWebhooks",
                    sortType: "DealerWebhookSortInput",
                    conditionsType: "DealerWebhookSearchInput",
                    customQuery: "dealerId",
                    dynamicQuery: true,
                }}
                uiConfig={{
                    hideSplit: true,
                    hideAllInPagination: true,
                }}
                selectionConfig={{
                    disabled: true
                }}
                exportConfig={{
                    fileName: "dealer-webhooks",
                }}
                customSearch={{ fields: ["dealerId"], terms: [String(dealerId)], exact: true, conditionOperator: "AND" }}
                onChangeConditions={handleChangeConditions}
                onChangeColumns={handleChangeColumns}
                height={height}
                tableButtons={["export", "orderColumn"]}
                extraButton={() =>
                    canCreate && (
                        <div style={{ margin: '0 5px' }}>
                            <Button
                                className="btn-dark-blue"
                                onClick={handleOpenCreate}
                            >
                                {messages['web_hooks_add_new'] || 'Add New Webhook'}
                            </Button>
                        </div>
                    )
                }
                searchConfig={{
                    conditions: ["name"],
                    optionValue: "id",
                    optionLabel: "name",
                    custom: { fields: ["dealerId"], terms: [dealerId], exact: true },
                }}
                defaultsConfig={{
                    sort: { field: 'id', order: 'DESC' },
                    rowsPerPage: 50,
                    valuesPerPage: [10, 25, 50, 100]
                }}
            />

            {openModalCreateEdit && <CreateEditWebHook tableRef={tableRef} />}
            {openModalDelete && <DeleteWebHook tableRef={tableRef} />}
        </div>
    );
};

export default WebHooksTab;
